/**
 * A Faceted Search returns a list of these objects along with the search results;
 * they can be displayed by searchFacetViews in a faceted search UI,
 * @class Netx.Models.SearchFacet
 * @extends Netx.Model
 * @see Netx.Collections.SearchFacetOptions
 */
import Base from '@netx/core/lib/netx-model';

import SearchFacetOptionsCollection from '../collections/searchFacetOptions';

export default class SearchFacet extends Base {
	/**
	 * @property {object}  defaults        - The default attribute values
	 * @property {number}  defaults.id     - Unique id
	 * @property {string}  defaults.label  - Facet attribute label
	 * @property {Netx.Collections.SearchFacetOptions} defaults.options - Facet options for attribute
	 * @property {string} defaults.label - Facet attribute label (client created)
	 */
	get defaults() {
		return {
			// id: null,
			label: '',
			options: null,
			title: '',
		};
	}

	/**
	 * Maintain Netx.Collections.SearchFacetOptions for this.attributes.options
	 * Checks if what is being set is, in part or whole, facet options - parses those out and passes the rest (if any) onto super
	 * @override
	 * @param {object|string} key     Model key string | Model keys/values object
	 * @param {mixed}         val     Value to set on model
	 * @param {object}        options Options for setting
	 */
	set(key, val, options) {
		// If key is an object and contains our options
		if (_.isObject(key) && key.options) {
			// Set the rest first so we have an id and all that jazz
			super.set(_.omit(key, 'options'), val, options);
			// Setup
			return this.parseOptions(key.options);
		}
		// If key is a single value and is options
		else if (key === 'options') {
			// Setup
			return this.parseOptions(val);
		}
		// Act as if
		return super.set(key, val, options);
	}

	/**
	 * Check for and create/reset Netx.Collections.SearchFacetOptions on this.attributes.options
	 * @method
	 * @param {object} options Facet options
	 * @fires module:asset#search:facet:option:changed
	 */
	parseOptions(options) {
		// Map our options to contain this id
		var facetId = this.id,
			facetLabel = this.get('label'),
			facetTitle = this.get('title');

		options = _.compact(
			_.map(!_.isArray(options) ? [options] : options, function (o) {
				// Only want facets options that matter
				if (o.count)
					return _.extend({}, o, {
						facetId: facetId,
						facetLabel: facetLabel,
						facetTitle: facetTitle,
					});
			}),
		);

		// If we haven't created the collection yet
		if (!(this.attributes.options || {}).isCollection) {
			// Create
			this.attributes.options = new SearchFacetOptionsCollection(options);
			this.addSubCollection(this.attributes.options);
			// Mimic sync
			this.attributes.options.trigger(
				'request:read',
				this.attributes.options,
				null,
			);
			this.attributes.options.trigger('sync', this.attributes.options, this);

			// When the options change I change
			this.listenTo(this.get('options'), 'change', function (model) {
				//this.trigger('change', this, model );
				if (model.changed && !_.isUndefined(model.changed.selected)) {
					dispatcher.trigger(
						'search:facet:option:changed',
						this,
						this.collection,
					);
				}
			});
		}
		// Already created, just need to update
		else {
			// Set
			this.attributes.options.set(options);
		}
	}
	/**
	 * Get selected facet options
	 * @method
	 * @return {array} Selected
	 */
	selectedOptions() {
		return this.get('options').getSelected();
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchFacet';
	}
}
